import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { setUrl } from "../features/web/web-slice";
import Results from "../results/Results";
import { isValidWebsite, normalizeWebsiteValue } from "../utils/url";

const analyzerSchema = z.object({
	url: z
		.string()
		.nonempty("El sitio es obligatorio")
		.transform((value) => normalizeWebsiteValue(value))
		.refine((value) => isValidWebsite(value), "Escribe una URL válida"),
});

type AnalyzerFormValues = z.infer<typeof analyzerSchema>;

function Main() {
	const url = useAppSelector((state) => state.web.url);
	const dispatch = useAppDispatch();
	const {
		handleSubmit,
		register,
		reset,
		watch,
		formState: { errors, isSubmitting, isValid },
	} = useForm<AnalyzerFormValues>({
		resolver: zodResolver(analyzerSchema),
		mode: "onChange",
		defaultValues: {
			url: "",
		},
	});

	const currentUrl = watch("url");

	const onSubmit = ({ url }: AnalyzerFormValues) => {
		dispatch(setUrl(url));
		reset();
	};

	if (url) {
		return <Results />;
	}

	return (
		<section className="page page--dashboard">
			<div className="page__header">
				<p className="eyebrow">Panel de optimización continua</p>
				<h1>Audita, prioriza y comparte mejoras de rendimiento</h1>
				<p>
					Conecta con la API oficial de PageSpeed y obtén recomendaciones basadas en
					Core Web Vitals para tu próximo release.
				</p>
			</div>

			<div className="dashboard__grid">
				<div className="panel analyzer-panel">
					<header className="panel__header">
						<div>
							<p className="eyebrow">1. Ingresa tu dominio</p>
							<h2>Auditoría instantánea</h2>
							<p>Usa solo el dominio, nosotros gestionamos el protocolo y la normalización.</p>
						</div>
						<span className="badge">Tiempo real</span>
					</header>

					<form
						onSubmit={handleSubmit(onSubmit)}
						className="analyzer-form"
						id="website-form"
						noValidate
					>
						<label htmlFor="site-input" className="sr-only">
							URL del sitio
						</label>
						<div className="analyzer-form__field">
							<span className="analyzer-form__prefix">https://</span>
							<input
								id="site-input"
								type="text"
								spellCheck="false"
								placeholder="midominio.com"
								autoComplete="off"
								className={clsx("analyzer-form__input", {
									"has-value": Boolean(currentUrl),
									"input-error": Boolean(errors.url),
								})}
								{...register("url")}
							/>
							<button
								type="submit"
								className="button button--primary"
								disabled={!isValid || isSubmitting}
							>
								Analizar
							</button>
						</div>
						{errors.url && (
							<span className="form-error" role="alert">
								{errors.url.message}
							</span>
						)}
					</form>
				</div>

				<div className="panel panel--secondary">
					<h3>Checklist de inicio rápido</h3>
					<ul className="pill-list">
						<li>Seguimiento histórico por usuario</li>
						<li>Alertas de regresión en segundos</li>
						<li>Exportación en JSON y CSV</li>
						<li>Métricas móviles y desktop</li>
					</ul>
					<p className="panel__footnote">
						Las auditorías usan caché corto (60 s) para respetar tus cuotas de PageSpeed y mantener la data fresca.
					</p>
				</div>
			</div>
		</section>
	);
}

export default Main;
